import { FunctionComponent, useState } from "react";
import { Contact } from "@type/core/dashboard/dashboard.types";
import DeleteIcon from "@static/core/icons/icon-delete.svg?react";
import { useTranslation } from "react-i18next";
import BasicButton from "@components/core/buttons/BasicButton";
import BasicModal from "@components/core/modals/BasicModal";
import i18n from "i18next";
import { Office } from "@type/core/offices/offices.types";
import Search from "antd/es/input/Search";
import { getContactLabel } from "@utils/helpers/text-helper";
import { ActiveUser } from "@type/core/auth/auth.types";

function filterContact(
  contact: Contact,
  search: string,
  authorizedExpertForConfidentiality: Contact[],
  officeId: string,
) {
  const isSelectedOffice = contact.officeId === officeId;
  return !!(
    (filterField(contact.firstname, search) ||
      filterField(contact.lastname, search) ||
      filterField(contact.email, search) ||
      filterField(contact.phone, search)) &&
    (!authorizedExpertForConfidentiality ||
      !(
        authorizedExpertForConfidentiality.filter(
          (expert) => expert.email === contact.email,
        ).length > 0
      )) &&
    isSelectedOffice
  );
}

function filterField(
  field: string | Office[],
  search: string,
): string | boolean {
  if (typeof field === "string") {
    return field && field.toLowerCase().includes(search);
  } else {
    return (
      field &&
      field.filter((office) => filterField(office.name, search)).length >= 1
    );
  }
}

interface ConfidentialityProps {
  detail?: boolean;
  confidential: boolean;
  authorizedExpertForConfidentiality: Contact[];
  initialAuthorizedExperts?: Contact[];
  isAuthorized: boolean;
  clearSelectedExpertForConfidentiality: (email: string) => void;
  addSelectedExpertForConfidentiality: (selectedExpert: Contact[]) => void;
  transactionStatus?: string;
  officeMembers?: Contact[];
  user?: ActiveUser;
  officeId: string;
  className?: string;
}

const Confidentiality: FunctionComponent<ConfidentialityProps> = ({
  detail = false,
  confidential,
  authorizedExpertForConfidentiality,
  initialAuthorizedExperts = [],
  isAuthorized,
  clearSelectedExpertForConfidentiality,
  addSelectedExpertForConfidentiality,
  transactionStatus,
  officeMembers = [],
  user,
  officeId,
  className = "",
}) => {
  const { t } = useTranslation();
  const members: Contact[] = officeMembers;
  const someoneElseToAdd =
    members.filter(
      (member) =>
        member.officeId === officeId &&
        !authorizedExpertForConfidentiality.some(
          (authorizedExpert) => authorizedExpert.email === member.email,
        ),
    ).length >= 1;
  const [search, setSearch] = useState<string>("");
  const [
    selectedExpertsForConfidentiality,
    setSelectedExpertsForConfidentiality,
  ] = useState<string[]>([]);
  const [isAddAuthorizationModalOpen, setIsAddAuthorizationModalOpen] =
    useState<boolean>(false);
  const filteredContacts: Contact[] = members
    .filter(
      (contact) =>
        filterContact(
          contact,
          search.toLowerCase(),
          authorizedExpertForConfidentiality,
          officeId,
        ) && contact.email !== user?.email,
    )
    .sort((a, b) => {
      if (a.lastname < b.lastname) {
        return -1;
      }
      if (a.lastname > b.lastname) {
        return 1;
      }
      if (a.firstname < b.firstname) {
        return -1;
      }
      if (a.firstname > b.firstname) {
        return 1;
      }
      return 0;
    });

  return !confidential && !detail ? null : (
    <div
      className={`app-confidentiality ${detail ? "ml-48" : "ml-24"} ${className}`}
    >
      <table className="mt-16 mb-8" data-test="confidentiality-list">
        <tbody>
          {confidential &&
            authorizedExpertForConfidentiality &&
            authorizedExpertForConfidentiality.length > 0 &&
            authorizedExpertForConfidentiality.map((authorizedExpert) => {
              if (
                authorizedExpert.firstname &&
                authorizedExpert.lastname &&
                authorizedExpert.email
              ) {
                return (
                  <tr key={authorizedExpert.email}>
                    <td className="with-bullet">
                      {`${authorizedExpert.firstname} ${authorizedExpert.lastname} (${authorizedExpert.email})`}
                    </td>
                    <td className="pl-10">
                      {isAuthorized &&
                        (!detail ||
                          !initialAuthorizedExperts.find(
                            (initialExpert) =>
                              initialExpert.email === authorizedExpert.email,
                          )) && (
                          <div
                            className="cursor-pointer"
                            onClick={() =>
                              clearSelectedExpertForConfidentiality(
                                authorizedExpert.email,
                              )
                            }
                          >
                            <DeleteIcon className="icon-30" />
                          </div>
                        )}
                    </td>
                  </tr>
                );
              }
              return undefined;
            })}
        </tbody>
      </table>
      {confidential && (
        <>
          {!detail && (
            <ul className="list-inline ml-24">
              {authorizedExpertForConfidentiality.length === 0 ? (
                <li className="list-inline-item">
                  {t("signature.accordion.confidential.onlyMe")}
                </li>
              ) : (
                isAuthorized && (
                  <li className="list-inline-item">
                    {t("signature.accordion.confidential.me")}
                  </li>
                )
              )}
            </ul>
          )}
          <ul className="list-inline">
            {transactionStatus !== "draft" && (
              <li className="list-inline-item">
                <BasicButton
                  disabled={!someoneElseToAdd}
                  variant="transparent-tertiary"
                  text={t("signature.accordion.confidential.addAuthorization")}
                  onClick={() => setIsAddAuthorizationModalOpen(true)}
                  className="ml-8"
                  title={
                    !someoneElseToAdd
                      ? t("signature.accordion.confidential.noOneElseToAdd")
                      : undefined
                  }
                />
              </li>
            )}
          </ul>
        </>
      )}
      <BasicModal
        visible={isAddAuthorizationModalOpen}
        onCancel={() => setIsAddAuthorizationModalOpen(false)}
        title={t("signature.accordion.confidential.modal.addAuthorization")}
        footer={
          <div className="modal-footer-buttons">
            <BasicButton
              variant="secondary"
              text={i18n.t("buttons.cancel")}
              onClick={() => setIsAddAuthorizationModalOpen(false)}
            />
            <BasicButton
              disabled={selectedExpertsForConfidentiality.length === 0}
              text={t("buttons.validate")}
              onClick={() => {
                const selectContact = filteredContacts.filter((contact) =>
                  selectedExpertsForConfidentiality.includes(contact.email),
                );
                if (selectContact) {
                  addSelectedExpertForConfidentiality(selectContact);
                }
                setIsAddAuthorizationModalOpen(false);
              }}
            />
          </div>
        }
      >
        <>
          <label htmlFor="search" className="d-inline-block mb-8">
            {t("signature.accordion.confidential.modal.search")}
          </label>
          <Search
            id="search"
            placeholder=""
            value={search}
            onChange={(e) => {
              e.preventDefault();
              setSearch(e.target.value);
            }}
            onPressEnter={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
            className="search-no-enter-button mb-16"
          />
          <label htmlFor="search" className="d-inline-block mb-8">
            {t("signature.accordion.confidential.modal.selection")}
          </label>
          <select
            name="contacts"
            id="contacts"
            value={selectedExpertsForConfidentiality}
            onChange={(e) =>
              setSelectedExpertsForConfidentiality([e.target.value])
            }
            className="select-multiple h-200px"
            multiple
          >
            {filteredContacts.map((contact) => (
              <option
                title={getContactLabel(contact)}
                className={
                  "text-nowrap" +
                  (selectedExpertsForConfidentiality.indexOf(contact.email) !==
                  -1
                    ? " selected"
                    : "")
                }
                key={contact.email}
                value={contact.email}
              >
                {getContactLabel(contact)}
              </option>
            ))}
          </select>
        </>
      </BasicModal>
    </div>
  );
};

export default Confidentiality;
